'use client';

import React, { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import FreeLessonNav from './FreeLessonNav';
import FreeLessonContent from './FreeLessonContent';
import { LessonContentBlock } from '@/app/module/1/components/LessonBlocks';
import { MODULE1_LESSONS } from '@/app/module/1/components/module1Data';
import { trackLessonStarted, trackLessonCompleted } from '@/lib/gaEvents';
import PracticeWithAnna from '@/components/PracticeWithAnna';

import { FREE_LESSONS } from './lessonData';

export default function FreeLessonLayout() {
  const searchParams = useSearchParams();
  const previewParam = searchParams?.get('preview');
  const router = useRouter();

  // previewLessonId: which module-1 lesson is being previewed (null = not in preview mode)
  const [previewLessonId, setPreviewLessonId] = useState<string | null>(null);
  // showSignupPrompt: shown after user clicks Next from lesson 1.2
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);

  const [activeLesson, setActiveLesson] = useState('free-1');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (previewParam === '1-1') {
      setPreviewLessonId('lesson-1-1');
    }
  }, [previewParam]);

  const currentFreeLesson = FREE_LESSONS.find((l) => l.id === activeLesson);
  const currentIdx = FREE_LESSONS.findIndex((l) => l.id === activeLesson);
  const isLastLesson = currentIdx === FREE_LESSONS.length - 1;

  // Fire lesson_started whenever the active lesson changes
  useEffect(() => {
    const lesson = FREE_LESSONS.find((l) => l.id === activeLesson);
    if (lesson) {
      trackLessonStarted({
        lesson_id: lesson.id,
        module_id: 'free',
        lesson_title: lesson.title,
      });
    }
  }, [activeLesson]);

  const handleNext = () => {
    if (currentIdx < FREE_LESSONS.length - 1) {
      const completedLesson = FREE_LESSONS[currentIdx];
      trackLessonCompleted({
        lesson_id: completedLesson.id,
        module_id: 'free',
        lesson_title: completedLesson.title,
      });
      setActiveLesson(FREE_LESSONS[currentIdx + 1].id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLessonClick = (id: string) => {
    setActiveLesson(id);
    setSidebarOpen(false);
    setPreviewLessonId(null);
    setShowSignupPrompt(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ─── Preview: Next lesson handler ─────────────────────────────────────────
  const handlePreviewNext = (currentId: string) => {
    if (currentId === 'lesson-1-1') {
      // Advance to lesson 1.2 (also free)
      setPreviewLessonId('lesson-1-2');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentId === 'lesson-1-2') {
      // 1.3+ is gated — show sign-up prompt
      setShowSignupPrompt(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBackFromPreview = () => {
    setPreviewLessonId(null);
    setShowSignupPrompt(false);
    router.replace('/free-lesson-screen');
  };

  // ─── Preview mode: sign-up prompt after lesson 1.2 ───────────────────────
  if (previewLessonId !== null || showSignupPrompt) {
    const previewLesson = previewLessonId
      ? MODULE1_LESSONS.find((l) => l.id === previewLessonId)
      : null;
    // lesson-1-2 is the last free preview; next step is sign-up
    const isLastFreePreview = previewLessonId === 'lesson-1-2';

    return (
      <div className="flex min-h-[calc(100vh-64px)]">
        <main className="flex-1 min-w-0">
          <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
            <button
              onClick={handleBackFromPreview}
              className="font-body text-xs mb-8 flex items-center gap-1 transition-colors duration-150 hover:opacity-70"
              style={{ color: '#9CA3AF' }}
            >
              ← Back to free lessons
            </button>

            {showSignupPrompt ? (
              // ── Sign-up / login gate after lesson 1.2 ──────────────────
              <div>
                <p className="font-body text-xs mb-4" style={{ color: '#888888' }}>
                  Module 1 Preview — Lessons 1.1 &amp; 1.2 Free
                </p>
                <div
                  className="rounded-2xl p-8 text-center"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E0D8',
                    boxShadow: '0 8px 32px rgba(27, 42, 74, 0.10)',
                  }}
                >
                  <div
                    className="w-10 h-0.5 rounded-full mx-auto mb-6"
                    style={{ backgroundColor: '#D4891A' }}
                    aria-hidden="true"
                  />
                  <p
                    className="font-display font-semibold text-xl mb-3"
                    style={{ color: '#1B2A4A', lineHeight: '1.35' }}
                  >
                    The rest of Module 1 is waiting.
                  </p>
                  <p
                    className="font-body text-sm leading-relaxed mb-2 max-w-sm mx-auto"
                    style={{ color: '#4B5563', lineHeight: '1.72' }}
                  >
                    Lessons 1.3 through 1.6 — plus eight more modules — are included with full access.
                  </p>
                  <p
                    className="font-body text-sm italic mb-8"
                    style={{ color: '#4B5563' }}
                  >
                    Pay once. Lifetime access.
                  </p>
                  <div className="flex flex-col gap-3 max-w-xs mx-auto">
                    <Link
                      href="/conversion-page"
                      className="block w-full text-center font-body text-sm font-semibold py-4 px-6 rounded-xl transition-all duration-200 active:scale-95"
                      style={{ backgroundColor: '#D4891A', color: 'white' }}
                    >
                      Get Full Access — $67 →
                    </Link>
                    <Link
                      href="/login"
                      className="block w-full text-center font-body text-sm font-semibold py-4 px-6 rounded-xl transition-all duration-200 active:scale-95"
                      style={{
                        border: '2px solid #1B2A4A',
                        color: '#1B2A4A',
                        backgroundColor: 'transparent',
                      }}
                    >
                      Already have an account? Log in →
                    </Link>
                    <button
                      onClick={handleBackFromPreview}
                      className="font-body text-xs text-center py-1.5 transition-colors duration-150 hover:opacity-70"
                      style={{ color: '#9CA3AF' }}
                    >
                      Back to free lessons
                    </button>
                  </div>
                </div>
              </div>
            ) : previewLesson ? (
              // ── Module 1 lesson preview (1.1 or 1.2) ───────────────────
              <div>
                <p className="font-body text-xs mb-4" style={{ color: '#888888' }}>
                  Module 1 Preview — Lesson {previewLesson.number} of 6
                </p>
                <LessonContentBlock
                  lessonId={previewLesson.id}
                  lessonNumber={previewLesson.number}
                  lessonTitle={previewLesson.title}
                  blocks={previewLesson.blocks}
                  onNextLesson={() => handlePreviewNext(previewLesson.id)}
                  isLastLesson={false}
                />
                {previewLesson.id === 'lesson-1-2' && (
                  <>
                    <PreviewPracticeToolIntroCard />
                    <div className="max-w-2xl mx-auto px-6 lg:px-10 pb-12">
                      <PracticeWithAnna
                        lessonData={{
                          moduleId: 1,
                          lessonId: '1-2',
                          lessonTitle: 'Copilot in Outlook',
                          topic: 'Copilot',
                          scenarioType: 'preloaded',
                        }}
                        isPaidUser={false}
                      />
                    </div>
                  </>
                )}
                {previewLesson.id === 'lesson-1-1' && (
                  <div className="max-w-2xl mx-auto px-6 lg:px-10 pb-12">
                    <PracticeWithAnna
                      lessonData={{
                        moduleId: 1,
                        lessonId: '1-1',
                        lessonTitle: 'What Copilot Actually Is',
                        topic: 'Copilot',
                        scenarioType: 'preloaded',
                      }}
                      isPaidUser={false}
                    />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </main>
      </div>
    );
  }

  // ─── Main free lesson flow ────────────────────────────────────────────────
  return (
    <div className="flex min-h-[calc(100vh-64px)]" style={{ backgroundColor: '#F9F7F4' }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <FreeLessonNav
        activeId={activeLesson}
        onLessonClick={handleLessonClick}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main content */}
      <main className="flex-1 min-w-0" style={{ backgroundColor: '#F9F7F4' }}>
        {/* Mobile header */}
        <div
          className="lg:hidden flex items-center gap-3 px-4 py-3 border-b sticky top-16 z-20"
          style={{ backgroundColor: '#F9F7F4', borderColor: '#E5E0D8' }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg font-body text-sm font-medium flex items-center gap-2 transition-colors duration-150"
            style={{ color: '#1B2A4A' }}
            aria-label="Open lesson navigation"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="2" y="4" width="14" height="1.5" rx="0.75" fill="currentColor"/>
              <rect x="2" y="8.25" width="10" height="1.5" rx="0.75" fill="currentColor"/>
              <rect x="2" y="12.5" width="12" height="1.5" rx="0.75" fill="currentColor"/>
            </svg>
            Lessons
          </button>
          {currentFreeLesson && (
            <span className="font-body text-sm truncate" style={{ color: '#9CA3AF' }}>
              {currentFreeLesson.title}
            </span>
          )}
        </div>

        {currentFreeLesson && (
          <FreeLessonContent
            lesson={currentFreeLesson}
            onNext={handleNext}
            isLastLesson={isLastLesson}
          />
        )}
      </main>
    </div>
  );
}

// ─── Practice Tool Intro Card (preview) ──────────────────────────────────────

function PreviewPracticeToolIntroCard() {
  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 pb-12">
      <div
        className="rounded-xl p-7"
        style={{ backgroundColor: 'var(--amber-pale)', border: '1px solid rgba(212,137,26,0.2)' }}
      >
        <span
          className="inline-block font-body text-xs uppercase tracking-widest mb-4 font-semibold"
          style={{ color: 'var(--amber)', letterSpacing: '0.12em' }}
        >
          Before You Try It For Real
        </span>
        <p
          className="font-body text-sm italic leading-relaxed whitespace-pre-line"
          style={{ color: 'var(--navy)', lineHeight: '1.85' }}
        >
          {`The first time I used AI properly, I was alone at my desk at 9pm hoping nobody would see me get it wrong.

You don't have to do it like that.

Below is a real AI tool, built into That Clicked. There's a sample email thread waiting. Hit the button and watch what happens. You've got three goes. Nobody's watching.`}
        </p>
        <p
          className="font-body text-xs font-semibold mt-5"
          style={{ color: 'var(--amber)' }}
        >
          — Anna
        </p>
      </div>
    </div>
  );
}